/**
 * Corrections layer (#186), client side. The server layers a correction
 * onto the verdict on the next walk; this mirrors that rule for the card the
 * person is looking at right now, so the correction they just saved shows
 * without a round trip — original kept as the first layer, newest last.
 */
import type { Annotation, DiagnoseDriver, DiagnoseEnvelope, EvidenceSource } from '../../apis';

export interface CorrectionInput {
  driver: string;
  driver_kind: 'metric' | 'segment' | 'event' | 'text';
  driver_label: string;
  /** a segment's metric — the way it reaches the target (#297) */
  driver_metric?: string | null;
  evidence_url?: string | null;
  note?: string | null;
}

export interface CorrectionLayer {
  layer: 'correction';
  id: string | null;
  asserted_by: string | null;
  asserted_by_name: string | null;
  asserted_at: string | null;
  driver: string;
  driver_kind?: string | null;
  driver_label?: string | null;
  evidence_url?: string | null;
  note?: string | null;
  verdict: any;
}

/** The annotation a correction is stored as: kind `correction`, bound to metric + window + perspective. */
export function correctionAnnotation(envelope: DiagnoseEnvelope, input: CorrectionInput, opts: { perspective?: string | null; sessionId?: string | null }) {
  const t = envelope.target;
  return {
    kind: 'correction',
    ts: t.window_start,
    ts_end: t.window_end,
    title: `Corrected: ${input.driver_label}`,
    body: input.note?.trim() || null,
    source: 'correction',
    scope: { metrics: [t.metric], ...(opts.perspective ? { perspective: opts.perspective } : {}) },
    confidence: 1,
    correction: {
      driver: input.driver, driver_kind: input.driver_kind, driver_label: input.driver_label,
      ...(input.driver_metric ? { driver_metric: input.driver_metric } : {}),
      evidence_url: input.evidence_url?.trim() || null,
      original_headline: (envelope.verdict_layers?.[0]?.verdict || envelope.verdict)?.headline || null,
      session_id: opts.sessionId || null,
      // the slice this verdict was about: the correction reaches this slice and no other — a root AND/OR group is one
      // slice and travels as the group it is, never flattened to "no filters"
      filters: Array.isArray(t.filters) ? t.filters : (t.filters && typeof t.filters === 'object' && Object.keys(t.filters).length ? t.filters : []),
    },
  };
}

/** A driver's identity on the card: a segment is the segment OF a metric, so "market=Phoenix" of B is not that of C. */
export function driverKey(d: Pick<DiagnoseDriver, 'kind' | 'id' | 'metric'>): string {
  return d.kind === 'segment' ? `segment:${d.id}:${d.metric || ''}` : `${d.kind}:${d.id}`;
}

/** Whether a driver on the card is the one a saved correction names — by name, and for a segment by its metric. */
export function namesDriver(d: DiagnoseDriver, corr: { driver?: string | null; driver_kind?: string | null; driver_metric?: string | null }): boolean {
  if (d.id !== corr.driver) return false;
  if (corr.driver_kind === 'segment' && corr.driver_metric) return d.kind === 'segment' && d.metric === corr.driver_metric;
  return true;
}

/**
 * The tier a corrected driver keeps (#297): the correction is a strong source, but the ceiling holds — an edge only a
 * signal made and nobody confirmed, or a path that crosses one, stays `unconfirmed`; a confirmed or exact edge is
 * `confirmed`; anything else is `emerging`.
 */
export function tierAfterCorrection(d: DiagnoseDriver): DiagnoseDriver['tier'] {
  if (d.capped_by) return 'unconfirmed';
  // an agent's word, wherever it was pasted (#296): a correction endorses it, it does not make it evidence
  if ((d.sources || []).some((s) => s.type === 'agent')) return 'unconfirmed';
  // a contribution keeps the tier its dry run earned (#193), a claim from the brain the tier its evidence earned (#296)
  if (d.annotation_kind === 'contribution' || d.annotation_kind === 'context') return d.tier;
  if (d.edge_provenance?.method === 'signal' && d.edge_status !== 'confirmed') return 'unconfirmed';
  return d.edge_status === 'confirmed' || d.edge_status === 'exact' ? 'confirmed' : 'emerging';
}

/** Lay a saved correction onto the envelope, the way the server does on the next walk. */
export function layerCorrection(envelope: DiagnoseEnvelope, saved: Annotation & Record<string, any>): DiagnoseEnvelope {
  const prov = saved.provenance || {};
  const corr = prov.correction || {};
  const original = envelope.verdict_layers?.[0]?.verdict || envelope.verdict;
  const who = prov.asserted_by_name || prov.asserted_by || 'someone';
  const when = String(prov.asserted_at || '').slice(0, 10);
  const label = corr.driver_label || corr.driver;
  const verdict = {
    kind: 'corrected' as const, confidence: 1,
    headline: `${label} — corrected by ${who} · ${when}${saved.body ? `: ${saved.body}` : ''}`,
    driver: corr.driver,
    corrected_by: { id: prov.asserted_by || null, name: prov.asserted_by_name || null, at: prov.asserted_at || null, correction_id: saved.id },
    original_kind: original?.kind,
  };
  const layer: CorrectionLayer = {
    layer: 'correction', id: saved.id, asserted_by: prov.asserted_by || null, asserted_by_name: prov.asserted_by_name || null,
    asserted_at: prov.asserted_at || null, driver: corr.driver, driver_kind: corr.driver_kind, driver_label: corr.driver_label,
    evidence_url: corr.evidence_url, note: saved.body, verdict,
  };
  const layers = [...(envelope.verdict_layers?.length ? envelope.verdict_layers : [{ layer: 'original' as const, verdict: original }]), layer];
  const src: EvidenceSource = { type: 'doc', detail: `corrected by ${who} · ${when}`, weak: false,
    meta: { correction_id: saved.id, asserted_by: prov.asserted_by, evidence_url: corr.evidence_url || undefined } };
  const drivers = envelope.drivers || [];
  const found = drivers.find((d) => namesDriver(d, corr));
  const rest = drivers.filter((d) => d !== found);
  const lead: DiagnoseDriver = found
    ? { ...found, sources: [src, ...(found.sources || [])], tier: tierAfterCorrection(found), corrected_by: verdict.corrected_by } as DiagnoseDriver
    : { kind: (['metric', 'segment', 'event', 'text'].includes(corr.driver_kind) ? corr.driver_kind : 'text') as DiagnoseDriver['kind'],
        id: corr.driver, label: label, tier: 'emerging', sources: [src], corrected_by: verdict.corrected_by } as DiagnoseDriver;
  return { ...envelope, verdict, verdict_layers: layers, corrections: [...(envelope.corrections || []), layer], drivers: [lead, ...rest] };
}
